import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from '../utils/toast';
import { confidentialAPI } from '../services/api';
import api from '../services/api';

/**
 * Custom hook for managing offboarding workflow state and operations
 * Consolidates duplicate workflow loading and saving logic across all offboarding steps
 */
const useOffboardingWorkflow = (workflowId) => {
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load workflow data from API
   * Standardizes response handling across all step components
   */
  const loadWorkflow = useCallback(async () => {
    if (!workflowId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await confidentialAPI.getOffboardingWorkflow(workflowId);

      // Standardized response handling for different API response formats
      if (response.data && response.data.success) {
        setWorkflow(response.data.workflow);
      } else if (response.status === 200 && response.data) {
        const wf = response.data.workflow || response.data;
        setWorkflow(wf);
      } else {
        throw new Error((response.data && response.data.error) || 'Failed to load workflow');
      }
    } catch (err) {
      console.error('Error loading workflow:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load workflow';
      setError(errorMessage);
      toast.error(errorMessage);
      // Navigate back to employee lifecycle on critical errors
      navigate('/admin/employee-lifecycle');
    } finally {
      setLoading(false);
    }
  }, [workflowId, navigate]);

  /**
   * Save step data with automatic file upload detection
   * @param {number} stepNumber - The step number (1-7)
   * @param {Object} stepData - The step data to save
   * @param {File} [file] - Optional file for upload (used in Step 1)
   * @returns {Promise<boolean>} Success status
   */
  const saveStep = useCallback(async (stepNumber, stepData, file = null) => {
    if (!workflowId) {
      toast.error('Workflow ID is missing');
      return false;
    }

    try {
      setSaving(true);
      setError(null);

      let response;

      // Handle file uploads (Step 1 resignation letter)
      if (file || stepData.resignation_letter_file) {
        const formData = new FormData();
        const fileToUpload = file || stepData.resignation_letter_file;
        
        formData.append('resignation_letter_file', fileToUpload);
        
        // Append other step data fields
        Object.keys(stepData).forEach(key => {
          if (key !== 'resignation_letter_file' && stepData[key] !== undefined && stepData[key] !== null) {
            formData.append(key, stepData[key]);
          }
        });

        response = await api.post(
          `/confidential/offboarding/${workflowId}/step-${stepNumber}/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        // Standard JSON update
        response = await confidentialAPI.updateOffboardingStep(workflowId, stepNumber, stepData);
      }

      // Check response success
      if (response.data && response.data.success) {
        toast.success(response.data.message || `Step ${stepNumber} saved successfully`);
        // Reload workflow to get updated data
        await loadWorkflow();
        return true;
      } else {
        throw new Error((response.data && response.data.error) || 'Failed to save step');
      }
    } catch (err) {
      console.error(`Error saving step ${stepNumber}:`, err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save step';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setSaving(false);
    }
  }, [workflowId, loadWorkflow]);

  /**
   * Reload workflow data (useful after external updates)
   */
  const reloadWorkflow = useCallback(() => {
    return loadWorkflow();
  }, [loadWorkflow]);

  // Load workflow on mount and when workflowId changes
  useEffect(() => {
    loadWorkflow();
  }, [loadWorkflow]);

  return {
    workflow,
    loading,
    saving,
    error,
    saveStep,
    reloadWorkflow
  };
};

export default useOffboardingWorkflow;

